import { notify } from '@zurich-base-ui/components.toast';
import { HttpError } from 'app/http';
import config from 'config';
import { ServicesData, SERVICE_NAMES } from './consts';
import PackageJSON from '../../../package.json';
import {
  IData,
  IEmailSummaryResponse,
  IExtractionResponse,
  IExtractionResult,
  IPutData
} from 'app/utils';
import { ISummaryResponse } from 'app/components/Sections/Summary';
import { IBusinessArea, IFeature, IServices } from './types';
import DefaultBusinessAreas from 'data/mock/businessAreaAndServices.json';
import DefaultServices from 'data/mock/services.json';
import { IMappedRetentionCallResponse } from 'app/components/Sections/RetentionCall';

type ErrorInput =
  | HttpError
  | Error
  | string
  | { message?: string }
  | null
  | undefined;

export function displayToastError(error: ErrorInput, title?: string): void {
  let message: string;
  if (error == null) {
    message = 'An unexpected error occurred';
  } else if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message ?? 'An unexpected error occurred';
  } else if (typeof error === 'object' && typeof error.message === 'string') {
    message = error.message ?? 'An unexpected error occurred';
  } else {
    message = 'An unexpected error occurred';
  }
  notify({ type: 'error', message, title });
}

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes <= 0) return '0 Bytes';

  const k = 1024;
  const safeDecimals = Math.max(0, decimals);
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const value = bytes / Math.pow(k, i);
  return `${Number.parseFloat(value.toFixed(safeDecimals))} ${sizes[i]}`;
};

export const smoothScrollingToAnchor = (anchor: string): void => {
  document
    .getElementById(anchor)
    ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
};

export const scrollToTop = (): void => {
  document
    .getElementsByTagName('main')[0]
    ?.scrollTo({ top: 0, behavior: 'smooth' });
};

export const exceedsMaxFileSize = (size: number): boolean => {
  return size >= config.APP.MAX_FILESIZE * 1024 ** 2;
};

export const handleFileSelection = (
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  fileType?: string,
  multiple?: boolean
): void => {
  const input = document.createElement('input');
  input.type = 'file';
  switch (fileType) {
    case SERVICE_NAMES.FNOL_CAPTURING:
    case SERVICE_NAMES.CALL_SUMMARY:
      input.accept = '.json,.txt,.mp4,.mp3,.wav,.m4a,.mpeg';
      break;
    case SERVICE_NAMES.ID_EXTRACT_PRO:
    case SERVICE_NAMES.INVOICE_EXTRACTOR:
      input.accept = '.png,.jpg,.jpeg,.bmp,.tiff,.heif,.pdf';
      break;
    case SERVICE_NAMES.RETENTION_CALL:
      input.accept = '.mp3';
      break;
    case SERVICE_NAMES.CATASTROPHE_DETECTION:
    case SERVICE_NAMES.TYPE_OF_LOSS:
      input.accept = '.xlsx';
      break;
    case SERVICE_NAMES.COVERAGE_CHECK_V2:
      input.accept = '.pdf';
      break;
    case SERVICE_NAMES.TRANSLATOR:
      input.accept = '.pdf, .txt, .docx';
      break;
    case SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING:
      input.accept = '.docx';
      break;
    case SERVICE_NAMES.ASK_YOUR_DOCS:
      input.accept = '.pdf, .docx, .pptx, .msg';
      break;
    default:
      input.accept = `.pdf`;
      break;
  }
  input.multiple = multiple ?? false;
  input.onchange = (e: any) => onChange(e);
  input.click();
};

export const getAllowedMimeTypes = (
  serviceName?: string
): { [key: string]: string[] } => {
  const pdfOnly = { 'application/pdf': ['.pdf'] };
  switch (serviceName) {
    case SERVICE_NAMES.FNOL_CAPTURING:
    case SERVICE_NAMES.CALL_SUMMARY:
      return {
        'application/json': ['.json'],
        'text/plain': ['.txt'],
        'video/mp4': ['.mp4'],
        'audio/mpeg': ['.mp3'],
        'audio/wav': ['.wav'],
        'audio/mp4': ['.m4a'],
        'video/mpeg': ['.mpeg']
      };
    case SERVICE_NAMES.ID_EXTRACT_PRO:
    case SERVICE_NAMES.INVOICE_EXTRACTOR:
      return {
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/bmp': ['.bmp'],
        'image/tiff': ['.tiff'],
        'image/heif': ['.heif'],
        ...pdfOnly
      };
    case SERVICE_NAMES.RETENTION_CALL:
      return { 'audio/mpeg': ['.mp3'] };
    case SERVICE_NAMES.CATASTROPHE_DETECTION:
    case SERVICE_NAMES.TYPE_OF_LOSS:
      return {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
          '.xlsx'
        ]
      };
    case SERVICE_NAMES.COVERAGE_CHECK_V2:
      return pdfOnly;
    case SERVICE_NAMES.TRANSLATOR:
      return {
        ...pdfOnly,
        'text/plain': ['.txt'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx']
      };
    case SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING:
      return {
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx']
      };
    case SERVICE_NAMES.ASK_YOUR_DOCS:
      return {
        ...pdfOnly,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          ['.pptx'],
        'application/vnd.ms-outlook': ['.msg']
      };
    default:
      return pdfOnly;
  }
};

export const validateMimeType = (file: File, serviceName?: string): boolean => {
  const allowedTypes = Object.keys(getAllowedMimeTypes(serviceName));
  return allowedTypes.includes(file.type);
};

export const hasSpecialCharacters = (fileName: string): boolean => {
  const specialCharacterRegex = /[^\w\s;ÄäÖöÜüß()[\]{}.,-]/;
  return specialCharacterRegex.test(fileName);
};

export const handleFileValidation = (
  file: File,
  serviceName?: string
): { valid: boolean; error?: string } => {
  if (exceedsMaxFileSize(file.size)) {
    return {
      valid: false,
      error: `File size limit is ${config.APP.MAX_FILESIZE}MB`
    };
  }
  if (!validateMimeType(file, serviceName)) {
    return { valid: false, error: 'Invalid file type' };
  }
  if (
    (serviceName === SERVICE_NAMES.ASK_YOUR_DOCS ||
      serviceName === SERVICE_NAMES.DOCUMENT_COMPARE_V2) &&
    hasSpecialCharacters(file.name)
  ) {
    return {
      valid: false,
      error: 'File name can not contain special characters'
    };
  }
  return { valid: true };
};

export function transformExtractionResult(
  extractionResult: IExtractionResult
): IData[] {
  return Object.entries(extractionResult).map(([key, value]) => ({
    key,
    value: value.value,
    updated: value.edited ?? false
  }));
}

export const cleanField = (field: string): string =>
  field
    ?.split(/\r\n|\n|\r/)
    .join('')
    .replaceAll('-', '') + ';';

export const cleanValue = (value: string): string => {
  const datePattern1 = /^\d{4}-\d{2}-\d{2}$/;
  const datePattern2 = /^\d{2}-\d{2}-\d{4}$/;

  if (datePattern1.test(value) || datePattern2.test(value)) {
    return value.split('-').join('/').split('-').join('') + ';';
  }
  return (
    value
      ?.replaceAll('€', 'EUR')
      .split(/\r\n|\n|\r/)
      .join('')
      .replaceAll('-', '') + ';'
  );
};

export const generateCSV = (
  element: IData[],
  filename: string,
  noExtension?: boolean
): void => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  let titleRow = '';
  let valueseRow = '';

  for (const field of element) {
    titleRow += cleanField(field.key);
    valueseRow += cleanValue(field.value);
  }

  titleRow = titleRow.slice(0, -1);
  titleRow += '\r\n';
  valueseRow = valueseRow.slice(0, -1);
  valueseRow += '\r\n';
  csvContent += titleRow + valueseRow;

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  if (noExtension) {
    link.setAttribute('download', `Data_Extraction_${filename}.csv`);
  } else {
    link.setAttribute(
      'download',
      `Data_Extraction_${filename.slice(0, -4)}.csv`
    );
  }
  document.body.appendChild(link);

  link.click();
};

export const generateCSVCallAnalysis = (
  element: Array<{ title: string; data: IMappedRetentionCallResponse[] }>,
  filename: string
): void => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  const headers = 'Evaluation;Evaluation type;Criteria;Thinking;Value\r\n';
  csvContent += headers;

  for (const evaluationKey of element) {
    const evaluation = evaluationKey.data;

    for (const section of evaluation) {
      const criterias = section.criterias;

      for (const criteria of criterias) {
        csvContent += cleanField(evaluationKey.title);
        csvContent += cleanField(section.section);
        csvContent += cleanField(criteria.criteria);
        csvContent += cleanField(criteria.thinking);
        csvContent += cleanField(criteria.value).slice(0, -1) + '\r\n';
      }
    }
  }

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `Data_Extraction_${filename.slice(0, -4)}.csv`);
  document.body.appendChild(link);

  link.click();
};

export const isAtTheBotton = (element: HTMLElement): boolean => {
  if (
    Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) <=
    100
  ) {
    return true;
  } else {
    return false;
  }
};
export const resetFieldsValues = (): void => {
  const textareas = document.getElementsByTagName('textarea');
  for (const field of Array.prototype.slice.call(textareas)) {
    field.value = '';
  }
};

export const handleResetFieldsBAsedOnExtractionType = (
  currentService: string | undefined,
  currentFile: File | undefined,
  currentEmailChain: string | undefined
): void => {
  if (currentService === undefined)
    globalThis.location.href = config.APP.PATH.LANDING;
  if (
    currentFile === undefined &&
    ['extraction', 'summary'].includes(
      ServicesData[String(currentService)].ExtractionType
    )
  ) {
    resetFieldsValues();
  }
  if (
    currentEmailChain === undefined &&
    ['email-summary'].includes(
      ServicesData[String(currentService)].ExtractionType
    )
  ) {
    resetFieldsValues();
  }
};

export const setFieldsValues = (
  originalExtraction: IExtractionResult
): void => {
  const RESIZING_MAX_VALUE = 90;

  for (const field in originalExtraction) {
    const summaryFieldElement: HTMLElement | null = document.getElementById(
      `field-${field}`
    );

    const textareaFieldElement: HTMLTextAreaElement | undefined =
      summaryFieldElement?.getElementsByTagName('textarea')[0];

    if (textareaFieldElement != null) {
      textareaFieldElement.style.height = 'auto';
      textareaFieldElement.value = originalExtraction[field].value;

      const numberOfLines = textareaFieldElement.value.split('\n').length;
      const valueLenght = textareaFieldElement.value.length;

      const shouldExpand =
        textareaFieldElement.id === 'textarea-Summary' ||
        numberOfLines > 1 ||
        valueLenght > RESIZING_MAX_VALUE;
      textareaFieldElement.style.height = shouldExpand
        ? `${textareaFieldElement.scrollHeight - 20}px`
        : '24px';
    }
  }
};

export const stringToHTML = (htmlString: string): HTMLElement => {
  const parser = new DOMParser();
  const html = parser.parseFromString(htmlString, 'text/html');

  return html.body;
};

export const isLastVersion = (): boolean => {
  const versionFromLocalStorage = localStorage.getItem('genAIBSVersion');
  if (
    versionFromLocalStorage === null ||
    PackageJSON.version !== versionFromLocalStorage
  ) {
    localStorage.clear();
    localStorage.setItem('genAIBSVersion', PackageJSON.version);
    return false;
  } else {
    return true;
  }
};

export const openFeedbackEmail = (): void => {
  const supportEmail = config.APP.SUPPORT_EMAIL;
  if (supportEmail === undefined) {
    displayToastError('', 'Support email was not found.');
    return;
  }
  globalThis.location.href = `mailto:${supportEmail}?subject=GenAI Business Services - Feedback and Support`;
};

export const onClaimsEmailResponse = (
  response: IEmailSummaryResponse,
  setExtraction: (saveOriginal: boolean, summary: IExtractionResult) => void
): void => {
  const summary = {
    Language: {
      value: response?.language
    },
    Summary: {
      value: response?.summary
    }
  };
  setExtraction(true, summary);
};

export const onExtractionSummaryResponse = (
  currentService: string | undefined,
  response: IExtractionResponse & ISummaryResponse,
  setExtraction: (
    saveOriginal: boolean,
    summary: IExtractionResult | IPutData,
    hash?: string
  ) => void
): void => {
  if (
    ServicesData[String(currentService)].ExtractionType === 'extraction' &&
    currentService !== 'id-extract-pro'
  ) {
    setExtraction(true, response.results, response.hash);
    const currentSummary: IPutData = {
      hash: response.hash,
      data: []
    };
    for (const field in response.results) {
      const value = {
        key: field,
        value: response.results[field].value,
        updated: false
      };
      currentSummary.data.push(value);
    }
    setExtraction(false, currentSummary);
  } else if (
    ServicesData[String(currentService)].ExtractionType === 'summary'
  ) {
    const summary: IExtractionResult = {
      Summary: {
        value: response.output
      }
    };
    setExtraction(true, summary);
  }
};

export const getInitials = (userName: string): string => {
  const splittedUserName = userName.split(' ');
  return splittedUserName[0]?.slice(0, 1) + splittedUserName[1]?.slice(0, 1);
};

export const mapBusinessAreas = (
  businessAreas: IBusinessArea,
  features: IFeature[] = []
): IBusinessArea => {
  const updatedBusinessAreas: IBusinessArea = {
    ...DefaultBusinessAreas.businessAreas
  };
  for (const [key, value] of Object.entries(businessAreas)) {
    if (key in updatedBusinessAreas) {
      updatedBusinessAreas[key] = {
        ...updatedBusinessAreas[key],
        ...value
      };
      const foundEnabledFeature = features.find(
        (feature: IFeature) =>
          updatedBusinessAreas[key].services?.includes(feature.name) &&
          feature.enabled
      );
      updatedBusinessAreas[key].active = !!foundEnabledFeature;
    }
  }

  return updatedBusinessAreas;
};

export const mapServices = (
  services: IServices,
  features: IFeature[] = []
): IServices => {
  const updatedServices: IServices = { ...DefaultServices.services };
  for (const [key, value] of Object.entries(services)) {
    const foundEnabledFeature = features.find(
      (feature: IFeature) => feature.name === key
    );
    const isFeatureEnabled = foundEnabledFeature?.enabled ?? false;
    if (key in updatedServices) {
      updatedServices[key] = {
        ...updatedServices[key],
        ...value
      };
    } else {
      updatedServices[key] = {
        ...value,
        active: isFeatureEnabled
      };
    }
    updatedServices[key].active = isFeatureEnabled;
    updatedServices[key].maturity = isFeatureEnabled
      ? value.maturity
      : undefined;
  }
  return updatedServices;
};

export const mapFeatures = (
  features: IFeature[] = []
): Record<string, boolean> => {
  const enabledFeatures: Record<string, boolean> = {};

  for (const feature of features) {
    enabledFeatures[feature.name] = feature.enabled;
  }

  return enabledFeatures;
};

export const onTranslationResponse = (
  response: any,
  setExtraction: (saveOriginal: boolean, translation: IExtractionResult) => void
): void => {
  setExtraction(true, {
    Translation: {
      value: response.result ?? ''
    }
  });
};

export const calculateMonthlyDifference = (
  currentValue: number,
  previousValue: number
): number => {
  if (previousValue === 0) {
    return currentValue === 0 ? 0 : 100;
  }
  return ((currentValue - previousValue) / previousValue) * 100;
};

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const transformText = (text: string): string => {
  return text.split('_').map(capitalize).join(' ');
};

export const getFileType = (fileName: string): string | undefined => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'txt':
      return 'text/plain';
    default:
      return undefined;
  }
};

export const formatTranslatedFileName = (internalName: string): string => {
  const baseName = internalName.split('_ChatGPT@zurich.com')[0];
  const hasExtension = internalName.includes('.');
  if (!hasExtension) return baseName;
  const extension = internalName.substring(internalName.lastIndexOf('.') + 1);
  return extension ? `${baseName}.${extension}` : baseName;
};

export const getBusinessAreaKey = (
  businessAreas: IBusinessArea,
  currentService: string
): string | undefined => {
  return Object.keys(businessAreas).find((key) =>
    businessAreas[key].services.includes(currentService)
  );
};

export const toggleDocumentInArray = (
  documents: string[],
  documentPath: string
): string[] => {
  if (documents.includes(documentPath)) {
    return documents.filter((doc) => doc !== documentPath);
  } else {
    return [...documents, documentPath];
  }
};

export const toggleMultipleDocuments = (
  documents: string[],
  documentPaths: string[]
): string[] => {
  const allSelected = documentPaths.every((path) => documents.includes(path));

  if (allSelected) {
    return documents.filter((doc) => !documentPaths.includes(doc));
  } else {
    const toAdd = documentPaths.filter((path) => !documents.includes(path));
    return [...documents, ...toAdd];
  }
};
